/**
 * Turning the place names on a transport leg into something comparable with a
 * destination's name. A leg records where you physically board and land — an
 * airport, a station, a terminal — and a destination records a city, so the
 * two rarely read the same even when they are.
 */

/**
 * Extract a clean city-level name for card titles.
 * Uses the explicit cityOverride when present (set by Google Places locality extraction).
 * Falls back to stripping common airport/station suffixes from the full place name.
 * e.g. "Brussels Midi" → "Brussels", "London St Pancras" → "London".
 *
 * It only strips known furniture, so a named terminal keeps its name:
 * "Berlin Schönefeld Airport" → "Berlin Schönefeld", not "Berlin". That's the
 * right call for a card title — it's what's printed on the ticket — and
 * matchesCity below covers the gap for matching. (The original comment here
 * claimed "Berlin"; it never did that.)
 */
export function cityLabel(full: string, cityOverride?: string | null): string {
  if (cityOverride) return cityOverride;
  const base = (full || '').split(',')[0].trim();
  const cleaned = base
    .replace(/\s+(international|airport|intl|aeropuerto|aéroport|aeroporto|flughafen|luchthaven|havalimanı)\b.*/gi, '')
    .replace(/\s+(hauptbahnhof|hbf|centraal|centrale|central\s+station)\b.*/gi, '')
    .replace(/\s+(midi|nord|sud|est|west|ost|öst)\b.*/gi, '')
    .replace(/\s+(st\.?\s+(pancras|lazare|eustache|enoch))\b.*/gi, '') // named saints on stations
    .replace(/\s+(station|stazione|estaci[oó]n|bahnhof|gare|terminus|halt)\b.*/gi, '')
    .replace(/\s+terminal(\s+\d+)?\b.*/gi, '')
    .trim();
  return cleaned || base;
}

/** Lowercase, accent-free, punctuation collapsed to single spaces, so
 *  "Zürich" and "Zurich" — or "St. Pancras" and "St Pancras" — compare equal. */
const fold = (value: string): string => (value || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Does this leg's boarding or landing place belong to this destination?
 *
 * Exact equality was the old test, and it failed constantly: a flight's `to`
 * is an airport ("Paris Charles de Gaulle"), not a city, so unless the parser
 * had filled `toCity` the leg looked like it belonged nowhere and dropped out
 * of the generated schedule entirely.
 *
 * Three ways to match, in order of confidence: the same name, the same name
 * once airport/station furniture is stripped, or the destination appearing at
 * the *start* of the place name — which is how terminals are named the world
 * over ("London Heathrow", "Tokyo Narita", "Berlin Schönefeld"). Anchoring to
 * the start is what stops "York" claiming a leg into "New York JFK".
 *
 * A terminal named without its city ("Charles de Gaulle Airport", "Schiphol")
 * still won't match — nothing here can know those, and guessing is worse than
 * the placeholder the schedule shows instead.
 */
export function matchesCity(place: string | undefined | null, destinationName: string): boolean {
  const destination = fold(cityLabel(destinationName || ''));
  const raw = fold(place || '');
  if (!destination || !raw) return false;
  if (raw === destination) return true;
  if (fold(cityLabel(place || '')) === destination) return true;
  return new RegExp(`^${escape(destination)}(\\s|$)`).test(raw);
}

/** Words that carry no identity, so a match resting only on them is no match:
 *  "The Garden Restaurant" and "The Corner Restaurant" share two of three. */
const GENERIC = new Set([
  'the', 'and', 'cafe', 'coffee', 'bar', 'pub', 'club', 'restaurant', 'bistro',
  'kitchen', 'house', 'hotel', 'grill', 'lounge', 'market', 'museum', 'gallery',
  'park', 'garden', 'gardens', 'centre', 'center', 'shop', 'store', 'temple',
  'church', 'tower', 'bridge', 'beach', 'street', 'road', 'place', 'tours',
]);

/**
 * Is this the same place under two names?
 *
 * The suggestion engines write a name from memory; Google returns the name of
 * the business it actually found. The two agree most of the time and disagree
 * in two different ways, only one of which matters:
 *
 *   "Da Mario" vs "Ristorante Da Mario"  — the same place, said longer
 *   "Café Lumière" vs "Boulangerie Paul" — not the same place at all
 *
 * A place-text search almost always returns SOMETHING for a query carrying a
 * city name, so "the lookup found nothing" is a weak test for whether a
 * suggestion is real. This is the strong one: the name that came back has to
 * be recognisably the name we asked for.
 *
 * Containment either way covers the first case. Failing that, every
 * distinctive word of the shorter name has to appear in the longer one —
 * distinctive meaning not "cafe", "the", "restaurant", the words that would
 * otherwise let two unrelated businesses match on their category alone.
 */
export function namesMatch(asked: string | undefined | null, found: string | undefined | null): boolean {
  // Apostrophes go before folding, not to a space with the rest of the
  // punctuation: "St. Mark's" and "St Marks" are the same name written twice,
  // and splitting the possessive into "mark s" makes them disagree.
  const strip = (value: string) => fold((value || '').replace(/['’]/g, ''));
  const a = strip(asked || '');
  const b = strip(found || '');
  if (!a || !b) return false;
  // Whole words only: as substrings, "The Rose" matched "Rosebud Motel" and
  // "Mira" matched "Miramar Hotel".
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  if (` ${long} `.includes(` ${short} `)) return true;
  const longWords = new Set(long.split(' '));
  const distinctive = short.split(' ').filter(word => word.length > 3 && !GENERIC.has(word));
  return distinctive.length > 0 && distinctive.every(word => longWords.has(word));
}
